import type { ReactElement } from 'react';
import React from 'react';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { noop } from 'lodash';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import type { EditConfigurationDialogComponentProps } from '~/components/core/Molecules/ConfigurableOption';
import type { NotesConfiguration } from '~/components/SystemConfiguration/NotesConfiguration/types';
import TextFieldFormik from '~/components/TextFieldFormik';
import { reportErrorInProductionOrThrow } from '~/Utils';

import { rowsPerPageOptions } from '../../Notes/NotesInner';

type NotesPerPageEditDialogValues = {
  default_notes_per_page: NotesConfiguration['default_notes_per_page'];
};
const NotesPerPageEditDialog = ({
  configuration,
  onCancel = noop,
  onSubmit,
}: EditConfigurationDialogComponentProps<NotesConfiguration>): ReactElement => {
  const { classes } = useStyles();

  const handleSubmit = async (
    values: NotesPerPageEditDialogValues,
    formikHelpers: FormikHelpers<NotesPerPageEditDialogValues>
  ) => {
    try {
      if (!onSubmit) {
        reportErrorInProductionOrThrow('No onSubmit function for NotePerPageDialog');
        return;
      }
      await onSubmit({
        default_notes_per_page: values.default_notes_per_page,
        is_default_notes_per_page_enabled: true,
      });
      onCancel();
    } catch {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <Formik<NotesPerPageEditDialogValues>
      initialValues={{ default_notes_per_page: configuration?.default_notes_per_page || 20 }}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <CardDialog
            title="Default notes per page"
            isDialog
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
            footerActions={
              <DialogFooterActions
                primaryLabel="Save"
                onClickPrimary={handleSubmit}
                onClickSecondary={onCancel}
                disabled={isSubmitting}
              />
            }
          >
            <TextFieldFormik
              id="default_notes_per_page"
              select
              label="Default notes per page"
              disabled={isSubmitting}
              className={classes.textField}
              fullWidth
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/*@ts-ignore*/}
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextFieldFormik>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

export default NotesPerPageEditDialog;
